import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { connect } from 'react-redux';

import TextActionAccordion from '../TextActionAccordion';
import BannerStyleCheckboxList, {
  BannerStyleCheckboxListProps,
} from '../BannerStyleCheckboxList';
import { ActionStateBannerStyleButtonProps } from '../ActionStateBannerStyleButton';

import { getTreatmentPage } from '../../app/actions/treatmentPage';
import useDebounce from '../../app/hooks/useDebounce';

import styles from './index.module.scss';

const DEBOUNCE_DELAY = 1200;

export interface BannerStyleCheckboxListsActionAccordionProps {
  activeStateTagText?: string;
  number: string;
  title: string;
  subtitle?: string;
  text?: string;
  isOpenByDefault?: boolean;
  leftButtonText?: string;
  buttonOnClick?: () => void;
  isButtonLoading?: boolean;
  leftButtonError?: object;
  completed: boolean;
  rightButtonText?: string;
  rightButtonUrl?: string;
  contentLinkText?: string;
  contentLinkUrl?: string;
  actionStateBannerStyleButton: ActionStateBannerStyleButtonProps;
  checkboxLists: BannerStyleCheckboxListProps[];
  className?: string;
  treatmentId?: string;
  stepId?: string;
  dispatch: any;
  isCheckboxToggleSuccessful: boolean;
  toggleCheckboxError?: object;
  inactive?: boolean;
}

const BannerStyleCheckboxListsActionAccordion: React.FC<
  BannerStyleCheckboxListsActionAccordionProps
> = ({
  activeStateTagText,
  number,
  title,
  subtitle,
  text,
  isOpenByDefault,
  leftButtonText,
  buttonOnClick,
  isButtonLoading,
  leftButtonError,
  completed,
  rightButtonText,
  rightButtonUrl,
  contentLinkText,
  contentLinkUrl,
  checkboxLists,
  className,
  treatmentId,
  stepId,
  dispatch,
  isCheckboxToggleSuccessful,
  toggleCheckboxError,
  inactive,
}) => {
  const [localCompleted, setLocalCompleted] = useState(completed);
  const [localCheckboxLists, setLocalCheckboxLists] = useState(checkboxLists);
  const [shouldSilentRefreshBeDone, setShouldSilentRefreshBeDone] =
    useState(false);

  const language = localStorage.getItem('hy-language') || 'en';

  const { debouncedCallback: debouncedCompletionCheck } = useDebounce(() => {
    setShouldSilentRefreshBeDone(true);
  }, DEBOUNCE_DELAY);

  useEffect(() => {
    if (toggleCheckboxError) {
      setLocalCheckboxLists(checkboxLists);
      setLocalCompleted(false);
      setShouldSilentRefreshBeDone(false);
    }
  }, [toggleCheckboxError, checkboxLists]);

  useEffect(() => {
    if (
      shouldSilentRefreshBeDone &&
      isCheckboxToggleSuccessful &&
      !toggleCheckboxError
    ) {
      setLocalCompleted(true);
      dispatch(getTreatmentPage(treatmentId, language, true));
      setShouldSilentRefreshBeDone(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldSilentRefreshBeDone,
    isCheckboxToggleSuccessful,
    toggleCheckboxError,
  ]);

  const handleCheckboxToggle = (listIndex: number, itemIndex: number) => {
    setLocalCheckboxLists((prevLists) => {
      const newLists = prevLists.map((list, currentListIndex) => {
        if (currentListIndex === listIndex) {
          return {
            ...list,
            items: list.items.map((item, currentItemIndex) => {
              if (currentItemIndex === itemIndex) {
                return { ...item, checked: !item.checked };
              }
              return item;
            }),
          };
        }
        return list;
      });

      const allCompleted = newLists.every((list) => {
        const items = list.items || [];
        return items.length > 0 && items.every((item) => item.checked);
      });

      if (allCompleted && !localCompleted) {
        debouncedCompletionCheck();
      }

      return newLists;
    });
  };

  return (
    <div className={cx(styles.wrapper, className)}>
      <TextActionAccordion
        title={title}
        subtitle={subtitle}
        isOpenByDefault={isOpenByDefault}
        text={text}
        number={number}
        activeStateTagText={activeStateTagText}
        leftButtonText={leftButtonText}
        buttonOnClick={buttonOnClick}
        isButtonLoading={isButtonLoading}
        leftButtonError={leftButtonError}
        completed={localCompleted}
        rightButtonText={rightButtonText}
        rightButtonUrl={rightButtonUrl}
        contentLinkText={contentLinkText}
        contentLinkUrl={contentLinkUrl}
      >
        {localCheckboxLists && localCheckboxLists.length > 0 && (
          <div
            className={cx(styles.checkboxListWrapper, {
              [styles.completed]: localCompleted,
            })}
          >
            {localCheckboxLists.map((checkboxList, listIndex) => (
              <BannerStyleCheckboxList
                key={listIndex}
                label={checkboxList.label}
                dayNumber={checkboxList.dayNumber}
                items={checkboxList.items}
                treatmentId={treatmentId}
                stepId={stepId}
                completed={localCompleted}
                listIndex={listIndex}
                onCheckboxToggle={handleCheckboxToggle}
                inactive={inactive}
              />
            ))}
          </div>
        )}
      </TextActionAccordion>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  isCheckboxToggleSuccessful: state.treatmentPage.get(
    'isCheckboxToggleSuccessful',
  ),
  toggleCheckboxError: state.treatmentPage.get('toggleCheckboxError'),
});

export default connect(mapStateToProps)(
  BannerStyleCheckboxListsActionAccordion,
);
